import ErrorBoundary from 'components/ErrorBoundary'
import { Footer } from 'components/Footer'
import Loader from 'components/Icons/LoadingSpinner'
import NavBar from 'components/NavBar'
import Popups from 'components/Popups'
import TopLevelModals from 'components/TopLevelModals'
import { DEFAULT_CHAIN_ID, isSupportedChain } from 'constants/chains'
import { useFeatureFlagsIsLoaded } from 'featureFlags'
import ApeModeQueryParamReader from 'hooks/useApeModeQueryParamReader'
import { Suspense, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import styled from 'styled-components/macro'
import DarkModeQueryParamReader from 'theme/components/DarkModeQueryParamReader'
import { flexRowNoWrap } from 'theme/styles'
import { Z_INDEX } from 'theme/zIndex'
import { useAccount, useSwitchChain } from 'wagmi'

import { privateRoutes } from './routes/privateRoutes'
import { publicRoutes } from './routes/publicRoutes'
import { renderPrivateRoute, renderPublicRoute } from './routes/renderRoutes'

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100dvh;
  padding-top: ${({ theme }) => theme.navHeight}px;
  align-items: center;
  flex: 1;
`

const HeaderWrapper = styled.div<{ transparent?: boolean }>`
  ${flexRowNoWrap};
  width: 100%;
  justify-content: space-between;
  position: fixed;
  top: 0;
  z-index: ${Z_INDEX.dropdown};
`

export default function App() {
  const { chainId } = useAccount()
  const isLoaded = useFeatureFlagsIsLoaded()
  const { switchChainAsync } = useSwitchChain()

  const { pathname } = useLocation()
  const [scrolledState, setScrolledState] = useState(false)

  useEffect(() => {
    window.scrollTo(0, 0)
    setScrolledState(false)
  }, [pathname])

  useEffect(() => {
    const scrollListener = () => {
      setScrolledState(window.scrollY > 0)
    }
    window.addEventListener('scroll', scrollListener)
    return () => window.removeEventListener('scroll', scrollListener)
  }, [])

  useEffect(() => {
    if (chainId && !isSupportedChain(chainId)) {
      switchChainAsync({ chainId: DEFAULT_CHAIN_ID })
    }
  }, [chainId, switchChainAsync])

  const isHeaderTransparent = !scrolledState

  return (
    <ErrorBoundary>
      <DarkModeQueryParamReader />
      <ApeModeQueryParamReader />
      <HeaderWrapper transparent={isHeaderTransparent}>
        <NavBar />
      </HeaderWrapper>
      <BodyWrapper>
        <Popups />
        <TopLevelModals />
        <Suspense fallback={<Loader />}>
          {isLoaded ? (
            <Routes>
              {publicRoutes.map(renderPublicRoute)}
              {privateRoutes.map(renderPrivateRoute)}
              <Route path="*" element={<Navigate to="/not-found" replace />} />
            </Routes>
          ) : (
            <Loader />
          )}
        </Suspense>
        <Footer />
      </BodyWrapper>
    </ErrorBoundary>
  )
}
